import type { CSSProperties } from 'react'
import { t } from '../i18n.ts'
import { stageColor } from './stageColor.ts'
import './StageFilter.css'

/**
 * Stage filter for the day list.
 *
 * Multi-select, because the question a festival-goer actually asks is "what is on
 * at Amfiet *and* Sirkus this afternoon", not "show me one stage". An empty
 * selection means no filter at all, so the default costs the user nothing and the
 * «Alle scener» chip is the way back.
 *
 * The counts are taken *after* the thumbs-up filter, so a chip never advertises
 * concerts that tapping it would not actually reveal.
 */
export function StageFilter({
  stages,
  selected,
  counts,
  onToggle,
  onClear,
}: {
  stages: readonly string[]
  selected: ReadonlySet<string>
  counts: ReadonlyMap<string, number>
  onToggle: (stage: string) => void
  onClear: () => void
}) {
  if (stages.length === 0) return null

  const none = selected.size === 0

  return (
    <div className="stage-filter" role="group" aria-label={t.day.stageFilterLabel}>
      <button
        type="button"
        className="stage-filter__chip stage-filter__chip--all"
        aria-pressed={none}
        data-on={none || undefined}
        onClick={onClear}
      >
        {t.day.allStages}
      </button>

      {stages.map((stage) => {
        const on = selected.has(stage)
        const count = counts.get(stage) ?? 0
        return (
          <button
            key={stage}
            type="button"
            className="stage-filter__chip"
            aria-pressed={on}
            data-on={on || undefined}
            data-empty={count === 0 || undefined}
            style={{ '--stage': stageColor(stage) } as CSSProperties}
            onClick={() => onToggle(stage)}
          >
            <span className="stage-filter__dot" aria-hidden="true" />
            <span className="stage-filter__name">{stage}</span>
            <span className="stage-filter__count">{count}</span>
          </button>
        )
      })}
    </div>
  )
}
